import { AdminHeader } from '@vgrubs/components';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { ListSpinner } from '../../adminComponents/AdminSpinner/adminSpinner';
import { NoDataComponent } from '../../adminComponents/NoDataComponent/NoDataComponent';
import { Modal } from '../../components/modal/modal';
import { lightTheme } from '../../styles/theme';
import type { ActiveAnnouncement } from '../../types/api/announcements.types';
import { AnnouncementView } from './components/AnnouncementView';
import { useAnnouncementsHistory } from './hooks/useAnnouncementsHistory';
import { WhatsNewCard } from './components/WhatsNewCard';
import { CardGrid, LoadError, RetryButton } from './WhatsNew.styled';

/**
 * Everything this app has announced, newest first - Settings > What's New,
 * the same place the merchant app keeps it.
 *
 * The backend picks the app + platform from the `app-version` header, so the
 * one page lists what admin or partner, whichever this build is, was told.
 * Reading it marks nothing as seen: a popup still due stays due.
 */
export const WhatsNew = () => {
  const {
    items,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  } = useAnnouncementsHistory();

  const [footerRef, footerInView] = useInView();
  // The announcement Read more opened, shown whole in the popup's own view.
  const [open, setOpen] = useState<ActiveAnnouncement | null>(null);

  // Not after a failed page: the footer is still in view when it fails, so
  // retrying from here would loop. The link under the list takes over.
  useEffect(() => {
    if (
      footerInView &&
      hasNextPage &&
      !isFetchingNextPage &&
      !isFetchNextPageError
    )
      fetchNextPage();
  }, [
    footerInView,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  ]);

  return (
    <>
      <AdminHeader title="What's New" />

      {isLoading && <ListSpinner loading mb="0" mbSM="0" />}

      {isError && !items.length && (
        <LoadError>
          Could not load the updates.{' '}
          <RetryButton type="button" onClick={() => refetch()}>
            Try again
          </RetryButton>
        </LoadError>
      )}

      {!isLoading && !isError && !items.length && (
        <NoDataComponent
          title="Nothing here yet"
          description="New features will show up here."
        />
      )}

      {!!items.length && (
        <CardGrid>
          {items.map((item) => (
            <WhatsNewCard key={item.id} item={item} onReadMore={setOpen} />
          ))}
        </CardGrid>
      )}

      {isFetchNextPageError && (
        <LoadError>
          Could not load older updates.{' '}
          <RetryButton type="button" onClick={() => fetchNextPage()}>
            Try again
          </RetryButton>
        </LoadError>
      )}

      {hasNextPage && !isFetchNextPageError && (
        <ListSpinner
          loading={isFetchingNextPage}
          observerRef={footerRef}
          mb="0"
          mbSM="0"
        />
      )}

      {/* The same modal and view the popup uses, so reading an old one looks
          exactly like it did when it first arrived. Nothing is marked seen. */}
      <Modal
        isOpen={!!open}
        closeModal={() => setOpen(null)}
        logo={false}
        width="720px"
        backgroundColor={lightTheme.white}
      >
        {open && (
          <AnnouncementView
            type={open.type}
            title={open.title}
            body={open.body}
            imageUrl={open.imageUrl}
            gifUrl={open.gifUrl}
            showDontShowAgain={false}
            onConfirm={() => setOpen(null)}
          />
        )}
      </Modal>
    </>
  );
};
